import { Menu, X } from 'lucide-react'
import { Button, ThemeToggle, cn, focusRing } from '@insightai/ui'
import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

const LINKS = [
  { to: '/solutions', label: 'Capabilities' },
  { to: '/solutions#solutions', label: 'Solutions' },
  { to: '/methodology', label: 'Methodology' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/resources', label: 'Resources' },
]

/**
 * M00-02 — sticky nav with `Sign In` ghost and `Get Started` primary at top right.
 *
 * The mobile drawer is a plain disclosure rather than a Radix Dialog: this must
 * render in prerendered HTML with no JS, and a portal-based modal renders nothing
 * server-side. `aria-expanded` + `aria-controls` give it the same semantics.
 */
export function StickyNav() {
  const [open, setOpen] = useState(false)
  const location = useLocation()

  // Close on navigation, or the drawer stays over the new page on mobile.
  useEffect(() => setOpen(false), [location.pathname])

  return (
    <header className="sticky top-0 z-drawer border-b border-border-subtle bg-surface-glass backdrop-blur-glass">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
        <Link
          to="/"
          className={cn('flex items-center gap-2 rounded-md text-body font-semibold', focusRing)}
        >
          <span
            aria-hidden
            className="flex size-7 items-center justify-center rounded-md bg-brand-solid text-caption font-bold text-text-on-brand"
          >
            iA
          </span>
          InsightAI
        </Link>

        <nav aria-label="Primary" className="ms-4 hidden items-center gap-1 lg:flex">
          {LINKS.map((link) => (
            <NavLink
              key={link.label}
              to={link.to}
              className={({ isActive }) =>
                cn(
                  'rounded-md px-3 py-2 text-body-sm transition-colors duration-fast',
                  isActive ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary',
                  focusRing,
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="ms-auto flex items-center gap-2">
          <ThemeToggle className="hidden sm:inline-flex" />
          <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex">
            <Link to="/auth/login">Sign In</Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/auth/signup">Get Started</Link>
          </Button>

          <button
            type="button"
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? 'Close navigation menu' : 'Open navigation menu'}
            onClick={() => setOpen((current) => !current)}
            className={cn('rounded-md p-2 text-text-secondary hover:text-text-primary lg:hidden', focusRing)}
          >
            {open ? <X aria-hidden className="size-5" /> : <Menu aria-hidden className="size-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          id="mobile-nav"
          aria-label="Primary mobile"
          className="border-t border-border-subtle bg-surface-raised px-4 py-3 lg:hidden"
        >
          <ul className="flex flex-col gap-1">
            {LINKS.map((link) => (
              <li key={link.label}>
                <Link
                  to={link.to}
                  className={cn(
                    'block rounded-md px-3 py-2 text-body-sm text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                    focusRing,
                  )}
                >
                  {link.label}
                </Link>
              </li>
            ))}
            <li className="pt-2">
              <Link
                to="/auth/login"
                className={cn(
                  'block rounded-md px-3 py-2 text-body-sm text-text-primary hover:bg-surface-hover',
                  focusRing,
                )}
              >
                Sign In
              </Link>
            </li>
          </ul>
          <div className="pt-3">
            <ThemeToggle />
          </div>
        </nav>
      )}
    </header>
  )
}
